// =============================================
// PÁGINA DE RELATÓRIO DE ABASTECIMENTOS
// =============================================

import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import FilterPanel from '../../components/Common/FilterPanel';
import ReportActions from '../../components/Reports/ReportActions';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { getToken, Config } from '../../services/config';
import { printReport } from '../../utils/printReport';
import './Reports.css';

const FuelReport: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [estatisticas, setEstatisticas] = useState<any>(null);
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    veiculoId: '',
    tipoCombustivel: ''
  });

  const {
    items: fuelData,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset,
    loadAll
  } = useInfiniteScroll<any>({
    fetchFunction: async (page, limit, appliedFilters) => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());

        Object.entries(appliedFilters || {}).forEach(([key, value]) => {
          if (value) {
            queryParams.append(key, String(value));
          }
        });

        const response = await apiService.getCustom<any>(
          `/relatorios/abastecimentos?${queryParams.toString()}`
        );

        if (response.success && response.data) {
          // Armazenar estatísticas na primeira carga
          if (page === 1 && response.data.estatisticas) {
            setEstatisticas(response.data.estatisticas);
          }

          return {
            success: true,
            data: {
              items: response.data.relatorio || [],
              pagination: {
                total: response.data.pagination?.total || response.data.relatorio?.length || 0
              }
            }
          };
        }

        throw new Error('Erro ao carregar relatório');
      } catch (err: any) {
        console.error('Erro ao buscar relatório:', err);
        setError(err.message || 'Erro ao carregar relatório');
        return {
          success: false,
          data: [],
          message: err.message || 'Erro ao carregar relatório'
        };
      }
    },
    itemsPerPage: 10,
    filters: filters
  });

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, String(value));
    });

    const token = getToken();

    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    const url = `${Config.API_BASE_URL}/relatorios/exportar/abastecimentos/csv?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-abastecimentos-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar CSV. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Verifique sua conexão.');
    }
  };

  const handleExportPDF = async () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, String(value));
    });

    const token = getToken();

    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    const url = `${Config.API_BASE_URL}/relatorios/exportar/abastecimentos/pdf?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-abastecimentos-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar PDF. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert('Erro ao exportar PDF. Verifique sua conexão.');
    }
  };

  const handlePrint = async () => {
    // Carrega TODOS os dados antes de imprimir
    await loadAll();
    // Aguarda um pouco para garantir que o DOM foi atualizado
    setTimeout(() => {
      printReport();
    }, 100);
  };

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num || 0);
  };

  // Carregar lista de veículos para o filtro
  useEffect(() => {
    const fetchVeiculos = async () => {
      try {
        const response = await apiService.getCustom<any>('/veiculos?limit=1000');
        if (response.success && response.data) {
          setVeiculos(response.data);
        }
      } catch (err) {
        console.error('Erro ao carregar veículos:', err);
      }
    };
    fetchVeiculos();
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <i className="fas fa-gas-pump"></i>
            Relatório de Abastecimentos
          </h1>
          <p className="report-subtitle">Análise de abastecimentos por veículo</p>
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Filtros e Ações</h2>
            <FilterPanel
              fields={[
                {
                  type: 'date',
                  label: 'Data Início',
                  value: filters.dataInicio,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataInicio: value }))
                },
                {
                  type: 'date',
                  label: 'Data Fim',
                  value: filters.dataFim,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataFim: value }))
                },
                {
                  type: 'select',
                  label: 'Veículo',
                  value: filters.veiculoId,
                  onChange: (value) => setFilters(prev => ({ ...prev, veiculoId: value })),
                  options: veiculos.map(v => ({ value: v.id, label: `${v.placa} - ${v.modelo || 'N/A'}` }))
                },
                {
                  type: 'select',
                  label: 'Tipo Combustível',
                  value: filters.tipoCombustivel,
                  onChange: (value) => setFilters(prev => ({ ...prev, tipoCombustivel: value })),
                  options: [
                    { value: 'Diesel', label: 'Diesel' },
                    { value: 'Diesel S10', label: 'Diesel S10' },
                    { value: 'Gasolina', label: 'Gasolina' },
                    { value: 'Etanol', label: 'Etanol' },
                    { value: 'GNV', label: 'GNV' }
                  ]
                }
              ]}
              onFilter={reset}
              onClear={() => {
                setFilters({
                  dataInicio: '',
                  dataFim: '',
                  veiculoId: '',
                  tipoCombustivel: ''
                });
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Relatório"
            />
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {!isLoading && fuelData && fuelData.length > 0 && (
              <>
                <ReportActions
                  onExportPDF={handleExportPDF}
                  onExportCSV={handleExportCSV}
                  onPrint={handlePrint}
                  loading={isLoading}
                />

                {estatisticas && (
                  <div className="report-statistics">
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-primary">
                        <i className="fas fa-truck"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Total de Veículos</h4>
                        <p className="stat-value">{estatisticas.total_veiculos}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-info">
                        <i className="fas fa-gas-pump"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Total Abastecimentos</h4>
                        <p className="stat-value">{estatisticas.total_abastecimentos}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-warning">
                        <i className="fas fa-tint"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Total de Litros</h4>
                        <p className="stat-value">{parseFloat(estatisticas.total_litros_geral || 0).toFixed(2)} L</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-success">
                        <i className="fas fa-dollar-sign"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Valor Total</h4>
                        <p className="stat-value">{formatCurrency(estatisticas.total_valor_geral)}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-primary">
                        <i className="fas fa-chart-line"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Média Valor/Litro</h4>
                        <p className="stat-value">{formatCurrency(estatisticas.media_valor_unitario_geral)}</p>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-info">
                        <i className="fas fa-tachometer-alt"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Média Consumo (Km/L)</h4>
                        <p className="stat-value">
                          {estatisticas.media_consumo_geral ? `${estatisticas.media_consumo_geral} Km/L` : 'N/A'}
                        </p>
                      </div>
                    </div>
                    {estatisticas.media_consumo_litro_hora_geral && (
                      <div className="stat-card">
                        <div className="stat-icon stat-icon-warning">
                          <i className="fas fa-hourglass-half"></i>
                        </div>
                        <div className="stat-content">
                          <h4>Média Consumo (L/h)</h4>
                          <p className="stat-value">
                            {estatisticas.media_consumo_litro_hora_geral} L/h
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="report-content">
                  <div className="report-table-header">
                    <h3>Dados Detalhados por Veículo</h3>
                  </div>
                  <div className="report-table-container">
                    {/* Contador de resultados */}
                    <ResultsCounter
                      displayedItems={displayedItems}
                      totalItems={totalItems}
                      isLoading={isLoading}
                      label="veículo(s)"
                    />

                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Placa</th>
                          <th>Modelo</th>
                          <th>Marca</th>
                          <th>Qtd. Abast.</th>
                          <th>Total Litros</th>
                          <th>Média R$/L</th>
                          <th>Total Gasto</th>
                          <th>Consumo (Km/L)</th>
                          <th>Consumo (L/h)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fuelData.map((item: any) => (
                          <tr key={item.veiculo_id}>
                            <td><strong>{item.placa}</strong></td>
                            <td>{item.modelo || 'N/A'}</td>
                            <td>{item.marca || 'N/A'}</td>
                            <td>{item.total_abastecimentos || 0}</td>
                            <td>{parseFloat(item.total_litros || 0).toFixed(2)} L</td>
                            <td>{formatCurrency(item.media_valor_unitario)}</td>
                            <td><strong>{formatCurrency(item.total_valor)}</strong></td>
                            <td>
                              {item.media_consumo_km_litro ? (
                                <span className="badge badge-info">
                                  {parseFloat(item.media_consumo_km_litro).toFixed(2)} Km/L
                                </span>
                              ) : (
                                <span className="badge badge-secondary">N/A</span>
                              )}
                            </td>
                            <td>
                              {item.media_consumo_litro_hora ? (
                                <span className="badge badge-warning">
                                  {parseFloat(item.media_consumo_litro_hora).toFixed(2)} L/h
                                </span>
                              ) : (
                                <span className="badge badge-secondary">N/A</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Botão "Ver Mais" */}
                    <LoadMoreButton
                      onClick={loadMore}
                      isLoading={isLoading}
                      hasMore={hasMore}
                    />
                  </div>
                </div>
              </>
            )}

            {isLoading && fuelData.length === 0 && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Carregando relatório...</p>
              </div>
            )}

            {!isLoading && fuelData.length === 0 && (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <p>Nenhum dado encontrado para os filtros selecionados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelReport;
